import path from 'path'
import consola, { Consola, WinstonReporter, Winston } from 'consola'
import { toUpper, toLower } from 'lodash'
import 'winston-daily-rotate-file'
import winston from 'winston'
import * as propertyStore from '../config/property-store'
import ApplicationProperties from '../config/app-properties'
import { LogLevel } from '../config/config-properties'
import * as webUtils from './web-utils'
import * as browserUtils from './client/browser-utils'
import * as coreUtils from './core-utils'
import * as functionUtils from './function-utils'

/**
 * 기본 로그 범위명
 * @ignore
 */
const DEFAULT_SCOPE_NAME = 'ustra'

/**
 * @category 내부객체
 */
const LOGGER_MAP: Record<string, Consola> = {}

let cachedProperty = null
const getProperty = () => {
  if (cachedProperty) {
    return cachedProperty
  }

  cachedProperty = propertyStore.getProperties()
  return cachedProperty
}

const _getLoggerKey = (scope?: string) => {
  const prop = getProperty()

  if (!scope) {
    scope = !prop ? DEFAULT_SCOPE_NAME : prop.logger.name
  }

  return `${scope}${webUtils.isBrowser() ? '-client' : '-server'}`
}

const _createWinstonLogger = (scope: string, prop: ApplicationProperties): Winston => {
  const { combine, timestamp, printf } = winston.format
  const format = printf(({ level, message, label, timestamp, args }) => {
    return `${timestamp} [${label || scope}] ${toUpper(level)}: ${message} ${args}`
  })

  // File 로깅을 하지 않을 경우, winston logger는 생성하지 않음.
  if (!prop.logger.file) {
    return null
  }

  // Client일 경우, file logging은 수행하지 않음.
  if (webUtils.isBrowser()) {
    return null
  }

  const logLevel = toLower(coreUtils.getObjectKey(LogLevel, prop.logger.level))
  const rotateOption = {
    // logger options
    level: logLevel,
    handleExceptions: true,
    json: false,
    // format: combine(label({ label: scope }), timestamp({ format: 'YYYY-MM-DD hh:mm:ss.SSS' }), format),
    format: combine(timestamp({ format: 'YYYY-MM-DD hh:mm:ss.SSS' }), format),

    // file roate options
    dirname: prop.logger.dirname ? prop.logger.dirname : path.join(prop.app.processPath, 'logs'),
    filename: prop.logger.filename,
    datePattern: prop.logger.datePattern,
    zippedArchive: prop.logger.zippedArchive,
    maxSize: prop.logger.maxSize,
    maxFiles: prop.logger.maxFiles,
  }

  const loggerOption = {
    level: logLevel,
    handleExceptions: true,
    transports: [
      // @ts-ignore
      new winston.transports.DailyRotateFile(rotateOption),
    ],
  }

  // add error rotate transport
  if (prop.logger.errFilename) {
    const errorRotateOption = coreUtils.deepMerge<any>({}, rotateOption)
    errorRotateOption.level = 'error'
    errorRotateOption.filename = prop.logger.errFilename

    loggerOption.transports.push(new winston.transports.DailyRotateFile(errorRotateOption))
  }
  return winston.createLogger(loggerOption)
}

const _hasWinstonReporter = (logger: Consola): boolean => {
  // eslint-disable-next-line dot-notation
  for (const reporter of logger['_reporters']) {
    if (reporter instanceof WinstonReporter) {
      return true
    }
  }

  return false
}

// consola logger 초기화 수행
const _initConsolaLogger = (scope?: string): Consola => {
  const prop = getProperty()
  scope = scope = !prop ? DEFAULT_SCOPE_NAME : prop.logger.name

  const loggerKey = _getLoggerKey(scope)

  if (LOGGER_MAP[loggerKey]) {
    return LOGGER_MAP[loggerKey]
  }

  const logger = consola.withScope(scope)
  logger.level = prop.logger.level

  // 2021/10/14 서버에상에서만 winston 사용 처리
  if (!webUtils.isBrowser()) {
    const winston = _createWinstonLogger(scope, prop)

    if (winston && !_hasWinstonReporter(logger)) {
      // winston 설정 수행
      logger.addReporter(new WinstonReporter(winston))
    }
  }

  // @ts-ignore
  if (!logger._wrapped) {

    // @ts-ignore
    logger._wrapped = true

    // @ts-ignore
    logger._log = functionUtils.wrap(logger._log, function (next) {
      // FIXME: Consola 버전 변경될 경우, traceCaller 변경 필요함.
      const caller = coreUtils.traceCaller(5)
      arguments[1].tag += `${arguments[1].tag ? '-' : ''}${caller}`
      next()
    })

  }


  LOGGER_MAP[loggerKey] = logger

  return logger
}

/**
 * get logger
 */
const logger = (scope?: string): Consola => {
  if (webUtils.isBrowser()) {
    if (browserUtils.isIEOldBrowser()) {
      // consola.wrapConsole()
      // @ts-ignore
      return console as Consola
    }
  }

  return _initConsolaLogger(scope)
}
logger.logger = logger
export default logger
